import React, { useCallback, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Both RELATIVE on purpose (the reason lib/i18n.js itself gives for its own relative
// re-export): "@/" is not mapped on the Jest side, so these two resolve natively in every
// suite that mounts the portal, and no test has to add a shim for them.
import { dateLocale } from "../i18n/dateLocale";
import usePolling from "../hooks/usePolling";

/** How often the thread is re-read while the tab is in the foreground (usePolling gates
 *  on visible + online and re-runs immediately on resume, so this is a ceiling on
 *  staleness for a reader who is actually looking, not a fixed request budget). */
const POLL_MS = 60000;
/** modules/messaging/schemas.BODY_MAX — the server's ceiling, mirrored so the counter
 *  below and the textarea's own `maxLength` cannot drift from it. */
const BODY_MAX = 5000;

const fmtWhen = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(dateLocale(lang), {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(iso);
  }
};

/**
 * Merge by `id`, order by `seq`.
 *
 * Every path into the thread goes through this one function — the first full read, a
 * `after=` poll delta, a `before=` page of older history, and the message the send itself
 * returns. That is what makes the optimistic append safe: the 201 carries the SERVER's
 * document, `seq` and all, so when the next poll ships the same message back it collapses
 * onto the same id instead of appearing twice. Ordering is by the server-minted `seq` and
 * never by arrival, so an out-of-order page cannot scramble the thread.
 */
const mergeMessages = (held, incoming) => {
  if (!incoming || incoming.length === 0) return held;
  const byId = new Map(held.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
};

/**
 * ClientConversation — the client's half of the two-way thread with the practice.
 *
 * ONE THREAD PER CLIENT, and the client never names it: `GET /api/client/conversation`
 * resolves it from the session's e-mail on the designated tenant (modules/messaging/
 * service.py's header explains why an id from the client would be the wrong key). So this
 * component has no conversation id, no selection, and no way to address somebody else's
 * thread.
 *
 * `conversation === null` IS NOT AN ERROR. It is the answer for every client who has never
 * written: the server does not create a thread until the first message, so the first visit
 * to this section is an empty state with a composer, not a failure. Treating it as one was
 * the obvious way to get this wrong — a 200 with a null body reads like a broken response.
 *
 * Polling, not sockets: 60 s through the shared `usePolling`, which suspends while the tab
 * is hidden or offline and runs immediately on resume. A send calls `refresh()` rather than
 * waiting out the interval, because the practitioner's reply to a message just sent is the
 * one a reader is actually watching for.
 *
 * @param {string} [practiceName]     what the practitioner's bubbles are signed with; the
 *   portal passes the same name it prints in its own header, so the two cannot disagree
 * @param {(n: number) => void} [onCountChange]  called with the number of messages held,
 *   for the hosting Section's counter
 */
export default function ClientConversation({ practiceName = "", onCountChange }) {
  const { lang } = useLanguage();
  const t = useT("public");

  // `loading` until the first read settles; `failed` only if THAT one failed. A later poll
  // failure must never blank a thread already on screen — usePolling keeps going, and the
  // next tick repairs it.
  const [phase, setPhase] = useState("loading"); // loading | ready | failed
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  // ONE slot for "the thing you just did failed" — a send, or a page of older history.
  // Distinct from `phase === "failed"`, which is "the thread itself could not be read".
  const [actionError, setActionError] = useState("");
  const [loadingOlder, setLoadingOlder] = useState(false);

  // The poll's cursor lives in a ref, not in state: `load` is registered with usePolling
  // once and must read the LATEST held messages on every tick without being rebuilt (a
  // new `fn` identity every render would be read through the hook's ref anyway, but the
  // cursor would still have to come from somewhere stable).
  const messagesRef = useRef([]);
  // The highest seq already sent to `PUT /read`, so a 60 s poll that sees the same
  // unread state does not re-announce it every minute.
  const markedRef = useRef(0);

  const applyMessages = useCallback((incoming) => {
    const next = mergeMessages(messagesRef.current, incoming);
    if (next === messagesRef.current) return next;
    messagesRef.current = next;
    setMessages(next);
    onCountChange?.(next.length);
    return next;
  }, [onCountChange]);

  /**
   * Announce what has been read — but only when there is something of the PRACTITIONER's
   * to read. `client_has_unread` is derived server-side as
   * `last_practitioner_seq > client_read_seq`, so gating on exactly that condition is what
   * keeps a client's own send from spending a request to mark their own words read.
   */
  const markRead = useCallback(async (conv, held) => {
    if (!conv || held.length === 0) return;
    if (conv.last_practitioner_seq <= conv.client_read_seq) return;
    const top = held[held.length - 1].seq;
    if (top <= markedRef.current) return;
    const previous = markedRef.current;
    markedRef.current = top;
    try {
      const { data } = await api.put("/client/conversation/read", { last_seen_seq: top });
      setConversation(data);
    } catch {
      // Put the marker back so the next tick retries: an unsent read receipt costs the
      // client a badge that will not clear, which is worse than one extra request.
      markedRef.current = previous;
    }
  }, []);

  const load = useCallback(async () => {
    const held = messagesRef.current;
    // `after` = the highest seq we hold. Nothing held (first read, or no thread yet) ⇒ a
    // full read, which the server answers with the NEWEST page (repository.list_messages).
    const after = held.length ? held[held.length - 1].seq : null;
    try {
      const { data } = await api.get(
        "/client/conversation",
        after === null ? undefined : { params: { after } },
      );
      const conv = data?.conversation ?? null;
      setConversation(conv);
      const next = applyMessages(data?.messages || []);
      setPhase("ready");
      await markRead(conv, next);
    } catch {
      setPhase((p) => (p === "loading" ? "failed" : p));
    }
  }, [applyMessages, markRead]);

  const { refresh } = usePolling(load, { intervalMs: POLL_MS });

  const send = useCallback(async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setActionError("");
    try {
      const { data } = await api.post("/client/conversation/messages", { body, locale: lang });
      // The server's own document, appended before the next poll can bring it: the thread
      // must show the message the instant it is accepted, not up to 60 s later.
      applyMessages([data]);
      setDraft("");
      refresh();
    } catch (err) {
      console.error(err);
      setActionError(err?.response?.status === 429
        ? t("portal.messages.rateLimited")
        : t("portal.messages.sendFailed"));
    } finally {
      setSending(false);
    }
  }, [applyMessages, draft, lang, refresh, sending, t]);

  const loadOlder = useCallback(async () => {
    const held = messagesRef.current;
    if (held.length === 0 || loadingOlder) return;
    setLoadingOlder(true);
    setActionError("");
    try {
      const { data } = await api.get("/client/conversation", { params: { before: held[0].seq } });
      applyMessages(data?.messages || []);
    } catch {
      setActionError(t("portal.messages.loadFailed"));
    } finally {
      setLoadingOlder(false);
    }
  }, [applyMessages, loadingOlder, t]);

  const onKeyDown = (e) => {
    // The composer is multi-line, so Enter must stay Enter; the modifier is the send.
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  const practitionerName = practiceName || t("portal.messages.practice");
  // `seq` is 1-based and contiguous per conversation (repository.allocate_seq_and_summarise
  // increments `last_seq`), so "the oldest message we hold is not #1" is an exact test for
  // "there is history above this page" — no count, no guessing.
  const hasOlder = messages.length > 0 && messages[0].seq > 1;

  return (
    <div data-testid="client-conversation">
      {phase === "loading" && (
        <p data-testid="conversation-loading" className="text-eh-muted flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> {t("portal.messages.loading")}
        </p>
      )}

      {phase === "failed" && (
        <p data-testid="conversation-load-failed" className="text-red-600 text-sm">
          {t("portal.messages.loadFailed")}
        </p>
      )}

      {/* THE EMPTY STATE, not an error: no thread exists until the first message. */}
      {phase === "ready" && messages.length === 0 && (
        <p data-testid="conversation-empty" className="text-eh-muted">
          {t("portal.messages.empty")}
        </p>
      )}

      {messages.length > 0 && (
        <>
          {hasOlder && (
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingOlder}
              data-testid="conversation-older"
              className="mb-4 text-sm font-medium text-eh-sage-deep hover:underline disabled:opacity-50"
            >
              {t("portal.messages.older")}
            </button>
          )}
          <ul className="space-y-3">
            {messages.map((m) => {
              const mine = m.author_kind === "client";
              return (
                <li
                  key={m.id}
                  data-testid={`message-row-${m.seq}`}
                  data-author={m.author_kind}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      mine
                        ? "bg-eh-sage/15 text-eh-ink rounded-br-md"
                        : "bg-white border border-eh-border-soft text-eh-ink rounded-bl-md"
                    }`}
                  >
                    <p className="text-xs text-eh-muted mb-1">
                      {mine ? t("portal.messages.you") : practitionerName}
                      {" · "}
                      {fmtWhen(m.created_at, lang)}
                    </p>
                    {/* `whitespace-pre-wrap`: the sender's own line breaks are part of
                        what they wrote. Rendered as TEXT, never as HTML — this body is
                        untrusted input from either party. */}
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div className="mt-6">
        <textarea
          rows={3}
          maxLength={BODY_MAX}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={t("portal.messages.placeholder")}
          aria-label={t("portal.messages.placeholder")}
          data-testid="conversation-composer"
          className="w-full rounded-2xl border border-eh-border-soft bg-white px-4 py-3 outline-none focus:border-eh-sage resize-none"
        />
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={send}
            disabled={sending || draft.trim().length === 0}
            data-testid="conversation-send"
            className="inline-flex items-center gap-2 rounded-full bg-eh-sage-deep text-white px-6 py-2.5 text-sm font-medium disabled:opacity-50"
          >
            {sending ? t("portal.messages.sending") : t("portal.messages.send")}
            <Send size={15} />
          </button>
          <span className="text-xs text-eh-muted">{t("portal.messages.sendHint")}</span>
          <span data-testid="conversation-charcount" className="ml-auto text-xs text-eh-muted">
            {t("portal.messages.charCount", { n: draft.length, max: BODY_MAX })}
          </span>
        </div>
        {actionError && (
          <p data-testid="conversation-error" className="mt-2 text-sm text-red-600">
            {actionError}
          </p>
        )}
      </div>
    </div>
  );
}
